// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import { noLonger, now } from '../../../../utils/react/propsComparisons';

// Selector
import { isGuestSelector } from '../../../../user/currentUserSelector';
import { getNotificationsFetchedTimestamp } from '../../../../notifications/notificationsSelector';
import {
    getSocketDisconnectionTime,
    getSocketReconnectionMode,
    socketConnectedSelector,
    socketDisconnectedSelector,
} from '../../../../utils/socket/socketConnectionSelector';

// Actions
import { fetchNotifications } from '../../../../notifications/notificationActions';

// Constants
import { TIMES } from '../../../../../common/utils/timeUtil';
import { SOCKET_RECONNECTION_MODE } from '../../../../utils/socket/socketConstants';

const socketIsNowConnected = now('isSocketConnected');
const socketIsNoLongerDisconnected = noLonger('isSocketDisconnected');

/**
 * Only fetch the notifications again:
 * - On re-connect, after a 2 mins disconnection (excl. interrupt time) and
 * - weren't already fetched in the last minute
 */
const shouldFetchNotifications = (prevProps, nextProps) => {
    const { notificationsFetchedTime } = nextProps;

    // Don't fetch if socket hasn't reconnected
    if (!socketIsNowConnected(prevProps, nextProps)) return false;

    // Don't fetch if it wasn't previously disconnected (e.g. was only an interrupt)
    if (!socketIsNoLongerDisconnected(prevProps, nextProps)) return false;

    // Don't fetch if been less than a 1 minute since last fetch
    const timeSinceLastFetch = Date.now() - notificationsFetchedTime;

    if (timeSinceLastFetch < TIMES.MINUTE) return false;

    // Don't fetch if it was disconnected for less than 2 mins
    const { disconnectionTime } = nextProps;
    const timeSinceDisconnected = disconnectionTime ? Date.now() - disconnectionTime : 0;
    if (timeSinceDisconnected < 2 * TIMES.MINUTE) return false;

    return true;
};

const mapStateToProps = () =>
    createStructuredSelector({
        socketReconnectionMode: getSocketReconnectionMode,
        notificationsFetchedTime: getNotificationsFetchedTimestamp,
        disconnectionTime: getSocketDisconnectionTime,
        isSocketConnected: socketConnectedSelector,
        isSocketDisconnected: socketDisconnectedSelector,
        isGuest: isGuestSelector,
    });

const mapDispatchToProps = (dispatch) => ({
    dispatchFetchNotifications: (args) => dispatch(fetchNotifications(args)),
});

export default (DecoratedComponent) => {
    @connect(mapStateToProps, mapDispatchToProps)
    class notificationsFetchDecorator extends React.Component {
        constructor(props) {
            super(props);
            this.fetchTimeoutId = null;
        }

        componentDidUpdate(prevProps) {
            const shouldFetch = shouldFetchNotifications(prevProps, this.props);

            if (shouldFetch) {
                if (this.fetchTimeoutId) clearTimeout(this.fetchTimeoutId);

                const timeDelay =
                    // If the fetch is occurring due to lost connection then randomly delay the fetching of
                    // notifications so we don't overwhelm the server
                    this.props.socketReconnectionMode === SOCKET_RECONNECTION_MODE.CONNECTION_LOST
                        ? Math.random() * 10 * TIMES.MINUTE
                        : 0;

                // Fetch sometime in the next 10 mins to ease load on servers after a WS server is stopped
                this.fetchTimeoutId = setTimeout(() => {
                    this.fetchNotifications();
                }, timeDelay);
            }
        }

        fetchNotifications = () => {
            const { isGuest, dispatchFetchNotifications, notificationsFetchedTime } = this.props;

            if (isGuest) return;

            dispatchFetchNotifications({ after: notificationsFetchedTime });
        };

        render() {
            return <DecoratedComponent {...this.props} />;
        }
    }

    notificationsFetchDecorator.propTypes = {
        isGuest: PropTypes.bool,
        isSocketConnected: PropTypes.bool,
        isSocketDisconnected: PropTypes.bool,
        lastPollTime: PropTypes.number,
        notificationsFetchedTime: PropTypes.number,
        dispatchFetchNotifications: PropTypes.func,
        disconnectionTime: PropTypes.number,
        socketReconnectionMode: PropTypes.string,
    };

    return notificationsFetchDecorator;
};
